import { Link } from "react-router-dom";
import { Check, X, Star } from "lucide-react";
import { PLANS } from "@/marketing/data";
import { useI18n } from "@/i18n/I18nContext";

function PlanCard({ plan }) {
  const { t } = useI18n();
  const elite = plan.highlight;
  return (
    <div
      data-testid={`plan-${plan.id}`}
      className={`relative rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 flex flex-col ${
        elite
          ? "bg-[#2C3E35] text-[#FDFBF9] shadow-[0_0_50px_rgba(212,175,55,0.18)] border border-[#D4AF37]/40 lg:scale-[1.03]"
          : "bg-white text-[#0A0A0A] border border-[#2C3E35]/10 shadow-[0_8px_32px_rgba(44,62,53,0.06)]"
      }`}
    >
      {elite && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#D4AF37] text-[#2C3E35] text-xs font-bold uppercase tracking-wider shadow-lg">
          <Star size={13} fill="currentColor" /> {t.pricing.mostPopular}
        </div>
      )}
      <h3 className="font-display text-2xl font-bold">{plan.name}</h3>
      <p className={`text-sm mt-1 ${elite ? "text-[#FDFBF9]/70" : "text-[#2C3E35]/70"}`}>{plan.tagline}</p>

      <div className="mt-6 flex items-end gap-2">
        <span className="font-display text-5xl font-extrabold tracking-tight">{plan.crc}</span>
        <span className={`pb-2 text-sm ${elite ? "text-[#FDFBF9]/60" : "text-[#2C3E35]/60"}`}>{t.pricing.perMonth}</span>
      </div>
      <p className={`text-sm ${elite ? "text-[#D4AF37]" : "text-[#B86B5A]"} font-medium`}>{plan.usd} USD{t.pricing.perMonth}</p>

      <div className={`mt-6 space-y-1.5 text-sm ${elite ? "text-[#FDFBF9]/85" : "text-[#2C3E35]"}`}>
        {plan.limits.map((l) => (
          <div key={l} className="flex items-center gap-2 font-medium">
            <span className={`h-1.5 w-1.5 rounded-full ${elite ? "bg-[#D4AF37]" : "bg-[#2C3E35]"}`} /> {l}
          </div>
        ))}
      </div>

      <Link
        to="/register"
        data-testid={`plan-cta-${plan.id}`}
        className={`mt-7 w-full text-center py-3 rounded-full font-semibold transition-all duration-300 ${
          elite
            ? "bg-[#D4AF37] text-[#2C3E35] hover:bg-[#e0bd4a]"
            : "bg-[#2C3E35] text-[#FDFBF9] hover:bg-[#0A0A0A]"
        }`}
      >
        {elite ? t.pricing.eliteCta : t.pricing.basicCta}
      </Link>

      <div className="mt-8 pt-6 border-t border-current/10">
        {plan.intro && <p className={`text-xs font-semibold uppercase tracking-wider mb-4 ${elite ? "text-[#D4AF37]" : "text-[#B86B5A]"}`}>{plan.intro}</p>}
        <ul className="space-y-3">
          {plan.features.map((f) => (
            <li key={f} className="flex items-start gap-2.5 text-sm">
              <Check size={17} strokeWidth={2.2} className={`mt-0.5 shrink-0 ${elite ? "text-[#D4AF37]" : "text-[#2C3E35]"}`} />
              <span className={elite ? "text-[#FDFBF9]/90" : "text-[#2C3E35]/90"}>{f}</span>
            </li>
          ))}
          {plan.excluded.map((f) => (
            <li key={f} className="flex items-start gap-2.5 text-sm opacity-50">
              <X size={17} strokeWidth={2.2} className="mt-0.5 shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function PricingCards() {
  const { lang } = useI18n();
  const plans = PLANS[lang] || PLANS.en;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto items-start">
      <PlanCard plan={plans.basic} />
      <PlanCard plan={plans.elite} />
    </div>
  );
}
